import { Fragment, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DaysOfCoverBadge, StockStatusBadge } from '@/components/inventory';
import { ProductIdentity } from '@/components/products';
import { WarehouseTypeBadge } from '@/components/locations';
import { LocationLink } from '@/components/locations';
import { MetricGrid, SummaryMetricCard, TablePagination, FormattedNumber } from '@/components/global';
import { formatNumber } from '@/lib/formatNumber';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Download,
  MapPin,
  Package,
  Search,
  Sliders,
  Store,
  Tags,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { downloadCsv } from '@/lib/csvExport';

type StockLocationType = 'HUB' | 'SPOKE' | 'VENUE';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

interface Venue {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

interface StockLocationRow {
  id: string;
  locationType: StockLocationType;
  locationId: string;
  productId: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  minLevel: number | null;
  maxLevel: number | null;
  maxCapacity: number | null;
  daysOfCover: number;
  location: {
    id: string;
    name: string;
    type: StockLocationType;
    code?: string | null;
  };
}

interface StockProductRow {
  productId: string;
  quantityOnHand: number;
  hubUnits: number;
  spokeUnits: number;
  venueUnits: number;
  quantityReserved: number;
  quantityAvailable: number;
  locationCount: number;
  product?: {
    id: string;
    sku: string;
    name: string;
    categoryId: string | null;
    category?: { id: string; name: string };
    thumbnailUrl?: string | null;
  };
  locations: StockLocationRow[];
}

interface StockOverviewSummary {
  totalRows: number;
  totalUnitsOnHand: number;
  uniqueProducts: number;
  hubUnits: number;
  spokeUnits: number;
  venueUnits: number;
}

interface StockOverviewResult {
  items: StockProductRow[];
  total: number;
  page: number;
  pageSize: number;
  summary: StockOverviewSummary;
}

function VenueFillBadge({
  quantityOnHand,
  maxCapacity,
}: {
  quantityOnHand: number;
  maxCapacity: number | null;
}) {
  if (quantityOnHand <= 0) {
    return (
      <StockStatusBadge quantityOnHand={0} minLevel={null} maxLevel={null} size="sm" />
    );
  }
  if (!maxCapacity || maxCapacity <= 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        In machines
      </span>
    );
  }
  const pct = Math.round((quantityOnHand / maxCapacity) * 100);
  const status = pct >= 60 ? 'green' : pct >= 30 ? 'amber' : 'red';
  const styles = {
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        styles[status]
      )}
    >
      {pct}% full
    </span>
  );
}

function QuantityCell({ value }: { value: number }) {
  return <FormattedNumber value={value} hideZero className="inline" />;
}

function LocationBreakdownTable({ locations }: { locations: StockLocationRow[] }) {
  return (
    <div className="rounded-md border bg-muted/20">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Location</TableHead>
            <TableHead className="text-right">On hand</TableHead>
            <TableHead className="hidden text-right md:table-cell">Reserved</TableHead>
            <TableHead className="text-right">Available</TableHead>
            <TableHead className="hidden md:table-cell">Days cover</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {locations.map((loc) => (
            <TableRow key={loc.id}>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <LocationLink
                      locationType={loc.location.type}
                      locationId={loc.location.id}
                      name={loc.location.name}
                    />
                    <WarehouseTypeBadge type={loc.location.type} size="sm" showIcon={false} />
                  </div>
                  {loc.location.code && (
                    <span className="text-xs text-muted-foreground font-mono">
                      {loc.location.code}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <FormattedNumber value={loc.quantityOnHand} />
              </TableCell>
              <TableCell className="hidden text-right text-muted-foreground md:table-cell">
                {loc.locationType === 'VENUE' ? (
                  '—'
                ) : (
                  <FormattedNumber value={loc.quantityReserved} />
                )}
              </TableCell>
              <TableCell className="text-right">
                <FormattedNumber value={loc.quantityAvailable} />
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <DaysOfCoverBadge days={loc.daysOfCover} size="sm" />
              </TableCell>
              <TableCell>
                {loc.locationType === 'VENUE' ? (
                  <VenueFillBadge
                    quantityOnHand={loc.quantityOnHand}
                    maxCapacity={loc.maxCapacity}
                  />
                ) : (
                  <StockStatusBadge
                    quantityOnHand={loc.quantityOnHand}
                    minLevel={loc.minLevel}
                    maxLevel={loc.maxLevel}
                    size="sm"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function StockLevelsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState<StockProductRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [summary, setSummary] = useState<StockOverviewSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(50);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [locationTypeFilter, setLocationTypeFilter] = useState(
    searchParams.get('locationType') || 'all'
  );
  const [locationFilter, setLocationFilter] = useState(
    searchParams.get('locationId') || 'all'
  );
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('categoryId') || 'all');
  const [lowStockOnly, setLowStockOnly] = useState(searchParams.get('lowStockOnly') === 'true');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const toggleExpanded = (productId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const loadStockOverview = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (locationTypeFilter && locationTypeFilter !== 'all') {
        params.set('locationType', locationTypeFilter);
      }
      if (locationFilter.startsWith('w:')) {
        params.set('warehouseId', locationFilter.slice(2));
      } else if (locationFilter.startsWith('v:')) {
        params.set('venueId', locationFilter.slice(2));
      }
      if (categoryFilter && categoryFilter !== 'all') params.set('categoryId', categoryFilter);
      if (lowStockOnly) params.set('lowStockOnly', 'true');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/stock/levels?${params}`);
      if (res.ok) {
        const data: StockOverviewResult = await res.json();
        setProducts(data.items);
        setTotal(data.total);
        setSummary(data.summary);
        setExpanded(new Set());
      }
    } catch (error) {
      console.error('Error loading stock overview:', error);
    } finally {
      setLoading(false);
    }
  }, [search, locationTypeFilter, locationFilter, categoryFilter, lowStockOnly, page, pageSize]);

  const loadFilters = useCallback(async () => {
    try {
      const [warehouseRes, venueRes, categoryRes] = await Promise.all([
        apiFetch('/api/v1/warehouses'),
        apiFetch('/api/v1/venues?pageSize=500'),
        apiFetch('/api/v1/categories'),
      ]);

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouses(data.items ?? data ?? []);
      }

      if (venueRes.ok) {
        const data = await venueRes.json();
        setVenues(data.items ?? data ?? []);
      }

      if (categoryRes.ok) {
        const data = await categoryRes.json();
        setCategories(Array.isArray(data) ? data : data.items ?? []);
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadStockOverview();
  }, [loadStockOverview]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (locationTypeFilter !== 'all') params.set('locationType', locationTypeFilter);
    if (locationFilter !== 'all') params.set('locationId', locationFilter);
    if (categoryFilter !== 'all') params.set('categoryId', categoryFilter);
    if (lowStockOnly) params.set('lowStockOnly', 'true');
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [
    search,
    locationTypeFilter,
    locationFilter,
    categoryFilter,
    lowStockOnly,
    page,
    setSearchParams,
  ]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function exportCsv() {
    if (products.length === 0) return;
    downloadCsv(
      `stock-overview-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Product',
        'SKU',
        'Category',
        'Total on hand',
        'Hub units',
        'Spoke units',
        'Venue units',
        'Reserved',
        'Available',
        'Locations',
      ],
      products.map((row) => [
        row.product?.name ?? '',
        row.product?.sku ?? '',
        row.product?.category?.name ?? '',
        row.quantityOnHand,
        row.hubUnits,
        row.spokeUnits,
        row.venueUnits,
        row.quantityReserved,
        row.quantityAvailable,
        row.locationCount,
      ])
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Stock Overview</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            One row per product with totals across hubs, spokes, and venues. Expand a row to see
            stock at each location.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={loading || products.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => navigate('/inventory/adjustments/new')}>
            <Sliders className="h-4 w-4 mr-2" />
            Adjust Warehouse Stock
          </Button>
        </div>
      </div>

      <Card>
        <MetricGrid className="lg:grid-cols-5">
          <SummaryMetricCard
            label="Unique products"
            value={summary?.uniqueProducts ?? 0}
            icon={Tags}
            isLoading={loading && !summary}
          />
          <SummaryMetricCard
            label="Total units"
            value={summary?.totalUnitsOnHand ?? 0}
            icon={Package}
            isLoading={loading && !summary}
          />
          <SummaryMetricCard
            label="In hubs"
            value={summary?.hubUnits ?? 0}
            icon={Building2}
            isLoading={loading && !summary}
          />
          <SummaryMetricCard
            label="In spokes"
            value={summary?.spokeUnits ?? 0}
            icon={Store}
            isLoading={loading && !summary}
          />
          <SummaryMetricCard
            label="At venues (machines)"
            value={summary?.venueUnits ?? 0}
            icon={MapPin}
            isLoading={loading && !summary}
          />
        </MetricGrid>
      </Card>

      <Card>
        <div className="p-4 flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
          <Select
            value={locationTypeFilter}
            onValueChange={(v) => {
              setLocationTypeFilter(v);
              setLocationFilter('all');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="HUB">Hubs</SelectItem>
              <SelectItem value="SPOKE">Spokes</SelectItem>
              <SelectItem value="VENUE">Venues</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={locationFilter}
            onValueChange={(v) => {
              setLocationFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {warehouses.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Warehouses</SelectLabel>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={`w:${w.id}`}>
                      {warehouseOptionLabel(w)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {venues.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Venues</SelectLabel>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={`v:${v.id}`}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={lowStockOnly ? 'low' : 'all'}
            onValueChange={(v) => {
              setLowStockOnly(v === 'low');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="All stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stock</SelectItem>
              <SelectItem value="low">Low / empty only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Product</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="hidden text-right md:table-cell">Hubs</TableHead>
              <TableHead className="hidden text-right md:table-cell">Spokes</TableHead>
              <TableHead className="hidden text-right md:table-cell">Venues</TableHead>
              <TableHead className="hidden text-right lg:table-cell">Locations</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No stock found for the current filters.
                </TableCell>
              </TableRow>
            ) : (
              products.map((row) => {
                const isExpanded = expanded.has(row.productId);

                return (
                  <Fragment key={row.productId}>
                    <TableRow>
                      <TableCell className="pr-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Hide locations' : 'Show locations'}
                          onClick={() => toggleExpanded(row.productId)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <ProductIdentity
                          name={row.product?.name ?? '—'}
                          sku={row.product?.sku}
                          product={row.product}
                          productId={row.productId}
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <FormattedNumber value={row.quantityOnHand} />
                      </TableCell>
                      <TableCell className="hidden text-right md:table-cell">
                        <QuantityCell value={row.hubUnits} />
                      </TableCell>
                      <TableCell className="hidden text-right md:table-cell">
                        <QuantityCell value={row.spokeUnits} />
                      </TableCell>
                      <TableCell className="hidden text-right md:table-cell">
                        <QuantityCell value={row.venueUnits} />
                      </TableCell>
                      <TableCell className="hidden text-right lg:table-cell">
                        <FormattedNumber value={row.locationCount} />
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={7} className="bg-muted/10 py-3 pl-4 pr-4 md:pl-12">
                          <LocationBreakdownTable locations={row.locations} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={total}
          itemLabel="products"
          pageSize={pageSize}
          onPageChange={setPage}
          summarySuffix={
            summary ? ` · ${formatNumber(summary.totalUnitsOnHand)} units total` : undefined
          }
        />
      </Card>
    </div>
  );
}
